import { apiVisitEngine } from '@/shared/api/client'
import type { IApiResponse } from '@/shared/types/api'
import type {
  IScreeningHistoryItem,
  IScreeningQueueParams,
  IScreeningQueueResponse,
  IScreeningResultsPayload,
  IScreeningResultsResponse,
} from '../model/screening.type'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/api/query-keys'

// ── Raw API ───────────────────────────────────────────────────────────────────

/** The queue endpoint defaults to ascending order, which pins page 0 to the oldest visits. */
export const SCREENING_QUEUE_DEFAULT_SORT = 'queued_at,desc'

export const screeningApi = {
  getScreeningQueue: async (
    params: IScreeningQueueParams,
  ): Promise<IScreeningQueueResponse> => {
    const query = new URLSearchParams()
    query.set('page', String(params.page))
    query.set('size', String(params.size))
    if (params.visit_type_id) query.set('visit_type_id', params.visit_type_id)
    if (params.status) query.set('status', params.status)
    if (params.donation_type_id)
      query.set('donation_type_id', params.donation_type_id)
    if (params.donor_category_id)
      query.set('donor_category_id', params.donor_category_id)
    if (params.gender) query.set('gender', params.gender)
    if (params.is_mobile_collection !== undefined)
      query.set('is_mobile_collection', String(params.is_mobile_collection))
    if (params.search) query.set('search', params.search)
    query.set('sort', params.sort ?? SCREENING_QUEUE_DEFAULT_SORT)

    const response = await apiVisitEngine.get<IScreeningQueueResponse>(
      `/v1/screening/queue?${query.toString()}`,
    )
    return response.data
  },

  saveScreeningResults: async (
    visitId: string,
    payload: IScreeningResultsPayload,
  ): Promise<IScreeningResultsResponse> => {
    const response = await apiVisitEngine.post<IScreeningResultsResponse>(
      `/v1/lab/screening/${visitId}/results`,
      payload,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data
  },

  getScreeningHistory: async (donorId: string) => {
    const response = await apiVisitEngine.get<
      IApiResponse<IScreeningHistoryItem[]>
    >(`/v1/lab/screening/donors/${donorId}/history`)
    return response.data.data
  },
}

export const useGetScreeningQueueQuery = (
  params: IScreeningQueueParams,
  { enabled }: { enabled: boolean },
) =>
  useQuery({
    queryKey: [...queryKeys.screening.queue(params)],
    queryFn: () => screeningApi.getScreeningQueue(params),
    enabled,
  })

export const useGetScreeningHistoryQuery = (donorId: string) =>
  useQuery({
    queryKey: queryKeys.screening.history(donorId),
    queryFn: () => screeningApi.getScreeningHistory(donorId),
    enabled: !!donorId,
  })

export const useSaveScreeningResultsMutation = (
  visitId: string,
  donorId: string,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: IScreeningResultsPayload) =>
      screeningApi.saveScreeningResults(visitId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.donors.detail(visitId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.donorCard.detail(donorId),
      })

      queryClient.invalidateQueries({
        queryKey: queryKeys.screening.history(visitId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.screening.all(),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.donors.all,
      })
    },
  })
}
